import { FC, useEffect, useRef, useState } from "react";
import { X, Download, Folder, FileText, ExternalLink } from "lucide-react";
import { Citation } from "@/constants/citation.constant";
import mammoth from "mammoth";
import useChatApiService from "@/services/chat.service";
import { PresignedUrl } from "@/interfaces/chat.interface";
import { BASE_VIEW_URL } from "@/constants/chat.constant";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Separator } from "../ui/separator";
import remarkGfm from "remark-gfm";
import { MemoizedReactMarkdown } from "../markdown/memoized-react-markdown";
import { loadPolicyLinkMap, resolvePolicyLink, getFileName } from "@/utils/policyLink";

function getDirectoryPath(fullPath: string) {
    const separatorIndex = fullPath.lastIndexOf('/');
    if (separatorIndex !== -1) {
        return fullPath.substring(0, separatorIndex + 1);
    }
    return '';
}

/**
 * Props for the CitationDetailsModal component
 */
interface Props {
  /** Whether the modal is open */
  open: boolean;
  /** Citation data to display */
  citation: Citation | null;
  /** Callback fired when modal is closed */
  onClose: () => void;
  /** Whether citation is loading. Defaults to false. */
  loading?: boolean;
}

/**
 * CitationDetailsModal component displays detailed information about a citation source
 * in a dialog modal. Supports viewing citation content, cover page, and downloading
 * the source file. Handles DOCX file extraction and PDF viewing.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {Citation | null} props.citation - Citation data to display
 * @param {() => void} props.onClose - Close callback
 * @param {boolean} [props.loading=false] - Whether citation is loading
 * @returns {JSX.Element | null} Dialog modal with citation details or null if not open
 * 
 * @example
 * ```tsx
 * <CitationDetailsModal
 *   open={isOpen}
 *   citation={citation}
 *   onClose={handleClose}
 *   loading={isLoading}
 * />
 * ```
 */
export const CitationDetailsModal: FC<Props> = ({
  open,
  citation,
  onClose,
  loading = false
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<'content' | 'cover'>('content');
  const [coverPageContent, setCoverPageContent] = useState<string>('');
  const [isLoadingCover, setIsLoadingCover] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfViewUrl, setPdfViewUrl] = useState('');
  const [externalPolicyLink, setExternalPolicyLink] = useState('');
  const { getPresignedUrl } = useChatApiService();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || !citation) {
      setExternalPolicyLink('');
      return;
    }

    let cancelled = false;

    loadPolicyLinkMap()
      .then((map) => {
        if (!cancelled) {
          setExternalPolicyLink(resolvePolicyLink(map, citation));
        }
      })
      .catch((error) => {
        console.error("Failed to load policy link map:", error);
        if (!cancelled) {
          setExternalPolicyLink('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, citation]);

  // Reset view mode when modal opens — open directly on the cover/document view
  useEffect(() => {
    if (!open) return;
    setViewMode('cover');
    setCoverPageContent('');
    setPdfViewUrl('');
    if (!citation?.file_path) return;
    (async () => {
      const rawPath = citation.file_path.trim();
      const payload: PresignedUrl = { blob_path: rawPath, expiry_hours: 2, permission: 'r' };
      const { presigned_url } = await getPresignedUrl("policy", payload);
      const isPdf = /\.pdf(?:$|\?)/i.test(rawPath);
      setPdfViewUrl(
        isPdf
          ? `${presigned_url}#toolbar=1&navpanes=0&view=FitH`
          : `${BASE_VIEW_URL}?src=${encodeURIComponent(presigned_url)}&wdZoom=50`
      );
    })();
  }, [open, citation?.file_path]);

  if (!open) return null;

  const handleDownload = async () => {
    const pathSource = citation?.file_path || citation?.source_file;
    
    if (!pathSource) {
      console.error('No file path available for download');
      return;
    }

    setIsDownloading(true);
    try {
      const rawPath = pathSource.trim();

      const payload: PresignedUrl = { blob_path: rawPath, expiry_hours: 2, permission: 'r' };
      const { presigned_url } = await getPresignedUrl("policy", payload);
      if (!presigned_url) throw new Error('No presigned URL returned');

      // Trigger browser download using the SAS URL directly
      const link = document.createElement('a');
      link.href = presigned_url;
      link.target = '_blank';
      link.rel = 'noopener';
      const fileName = citation.name || citation.source_file?.split('/').pop() || 'source-file';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      if (citation?.url) {
        window.open(citation.url, '_blank');
      } else {
        console.error('No fallback URL available');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const getSourceTitle = () => {
    if (citation?.source_file) {
      const decoded = decodeURIComponent(citation.source_file);
      return decoded.length > 60 ? decoded.substring(0, 50) + '...' : decoded;
    }
    if (citation?.name) return decodeURIComponent(citation.name);
    if (citation?.content) {
      const firstLine = citation.content.split('\n')[0].trim();
      return firstLine.length > 60 ? firstLine.substring(0, 50) + '...' : firstLine;
    }
    return 'Citation Details';
  };

  const getFileFormat = () => {
    if (citation?.url) {
      // Remove query parameters and get file extension
      const urlWithoutParams = citation.url.split('?')[0];
      const extension = urlWithoutParams.split('.').pop()?.toLowerCase();

      // Check if extension is a valid file type
      const validExtensions = ['docx', 'doc', 'pdf', 'txt', 'rtf'];
      if (extension && validExtensions.includes(extension)) {
        return extension.toUpperCase();
      }

      // If no valid extension, check URL path for file types
      const urlLower = urlWithoutParams.toLowerCase();
      if (urlLower.includes('.docx')) {
        return 'DOCX';
      } else if (urlLower.includes('.pdf')) {
        return 'PDF';
      } else if (urlLower.includes('.doc')) {
        return 'DOC';
      } else if (urlLower.includes('.txt')) {
        return 'TXT';
      } else if (urlLower.includes('.rtf')) {
        return 'RTF';
      }

      // For Azure blob storage URLs without clear extensions, default to DOCX
      // since most policy documents are DOCX files
      if (urlLower.includes('blob.core.windows.net') || urlLower.includes('chop-genai-policy-documents')) {
        return 'DOCX';
      }

      return 'DOCX';
    }
    return 'DOCX';
  };

  const extractCoverPageContent = async () => {
    if (!citation?.url) return;

    setIsLoadingCover(true);
    try {
      const downloadUrl = citation?.url;

      let response;
      try {
        response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/octet-stream, */*'
          },
          mode: 'cors',
          cache: 'no-cache'
        });
      } catch (corsError) {
        // If CORS fails, we can't read the response body for extraction
        throw new Error('CORS restrictions prevent file access. Please use the download button to view the file content.');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');

      // Check if it's a DOCX file
      const isDocx = contentType?.includes('wordprocessingml') ||
        contentType?.includes('octet-stream') ||
        downloadUrl.toLowerCase().includes('.docx');

      if (!isDocx) {
        throw new Error('File is not a DOCX document. Only DOCX files are supported for cover page extraction.');
      }

      const arrayBuffer = await response.arrayBuffer();

      if (arrayBuffer.byteLength === 0) {
        throw new Error('Empty file received');
      }

      // Extract text from docx using mammoth
      const result = await mammoth.extractRawText({ arrayBuffer });
      const text = result.value;

      if (!text || text.trim().length === 0) {
        setCoverPageContent('No text content found in the document.');
        return;
      }

      // Get first page content (first few paragraphs)
      const paragraphs = text.split('\n\n').filter(p => p.trim().length > 0);
      const firstPageContent = paragraphs.slice(0, 3).join('\n\n');
      setCoverPageContent(firstPageContent);
    } catch (error) {
      console.error('Failed to extract cover page content:', error);
    } finally {
      setIsLoadingCover(false);
    }
  };

  const handleViewToggle = async () => {
    if (viewMode === 'content') {
      setViewMode('cover');
      if (!citation?.file_path) return;
      const rawPath = citation.file_path.trim();
      const payload: PresignedUrl = { blob_path: rawPath, expiry_hours: 2, permission: 'r' };
      const { presigned_url } = await getPresignedUrl("policy", payload);
      setPdfViewUrl(`${BASE_VIEW_URL}?src=${encodeURIComponent(presigned_url)}&wdZoom=50`);
    }
    // else {
    //   setViewMode('content');
    // }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="md:!max-w-[68vw] md:w-[68vw] md:h-[90vh] h-full flex flex-col">
        <DialogTitle className="sr-only">
          {getFileName(citation?.source_file || citation?.file_path || '')}
        </DialogTitle>
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 rounded-lg hover:bg-gray-100 active:bg-gray-200 p-2"
        >
          <X className="text-[#071934]" />
        </button>
        <div className="flex items-start justify-between gap-3 pr-12">
          <DialogDescription asChild>
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 flex-1 min-w-0 space-y-1">
              {(() => {
                const dir = citation ? getDirectoryPath(citation.file_path || citation.source_file || '') : '';
                const decoded = (() => {
                  try { return decodeURIComponent(dir); } catch { return dir; }
                })();
                const segments = decoded.split('/').filter(Boolean);
                const fileName = getFileName(citation?.source_file || citation?.file_path || '');
                return (
                  <>
                    {segments.map((seg, i) => (
                      <div
                        key={`folder-${i}`}
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${i * 16}px` }}
                      >
                        <Folder size={16} className="flex-shrink-0" />
                        <span className="break-all" title={seg}>{seg}</span>
                      </div>
                    ))}
                    {fileName && (
                      <div
                        className="flex items-center gap-2 text-gray-900 dark:text-gray-100 text-base font-semibold pt-1"
                        style={{ paddingLeft: `${segments.length * 16}px` }}
                      >
                        <FileText size={18} className="flex-shrink-0 text-primary-500" />
                        <span className="break-all" title={fileName}>{fileName}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </DialogDescription>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            aria-label="Download source file"
            className="flex items-center gap-2 px-3 py-2 bg-primary-500 text-white text-sm rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isDownloading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{isDownloading ? 'Downloading...' : 'Download'}</span>
          </button>
          {externalPolicyLink && (
            <a
              href={externalPolicyLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open source on PolicyMedical"
              className="flex items-center gap-2 px-3 py-2 border border-primary-500 text-primary-600 text-sm rounded-md hover:bg-primary-50 flex-shrink-0"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Open in PolicyManager</span>
            </a>
          )}
        </div>
        <Separator className="mt-3"></Separator>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden break-words">
          {loading || isLoadingCover ? (
            <div className="flex items-center justify-center py-8 flex-1">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : viewMode === 'cover' ? (
            <div className="prose max-w-none rounded-md text-justify flex-1 flex flex-col min-h-0 h-full">
              <div className="flex-1 min-h-0 my-3">
                <div
                  className="origin-top-left min-h-[calc(100svh-200px)] md:min-h-[calc(100svh-400px)]"
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <iframe
                    src={pdfViewUrl}
                    width="100%"
                    height="100%"
                  ></iframe>
                </div>
              </div>
            </div>
          ) : citation?.content ? (
            <div className="prose max-w-none bg-[#F6F9FE] rounded-md text-justify flex-1 flex flex-col min-h-0 h-full">
              <div className="text-lg font-semibold flex-shrink-0 p-3">Content</div>
                <div className="text-sm leading-relaxed overflow-y-auto flex-1 px-3 pb-3 min-h-[calc(100svh-200px)] md:min-h-[calc(100svh-400px)] md:max-h-[calc(100svh-300px)]">
                  <MemoizedReactMarkdown
                    className="prose dark:prose-invert max-w-none text-sm"
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1({ children }) {
                        return <h1 className="text-xl font-bold mt-4 first:mt-0 mb-2">{children}</h1>;
                      },
                      h2({ children }) {
                        return <h2 className="text-lg font-bold mt-3 first:mt-0 mb-2">{children}</h2>;
                      },
                      h3({ children }) {
                        return <h3 className="text-base font-bold mt-3 first:mt-0 mb-1">{children}</h3>;
                      },
                      ol({ children }) {
                        return <ol className="list-decimal my-2 ml-6">{children}</ol>;
                      },
                      ul({ children }) {
                        return <ul className="list-disc my-2 ml-6">{children}</ul>;
                      },
                      li({ children }) {
                        return <li className="my-0.5">{children}</li>;
                      },
                      p({ children }) {
                        return <p className="my-2">{children}</p>;
                      },
                      table({ children }) {
                        return (
                          <table className="border-collapse border border-gray-300 dark:border-gray-600 my-2 w-full">
                            {children}
                          </table>
                        );
                      },
                      th({ children }) {
                        return (
                          <th className="border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-2 py-1 text-left text-xs font-semibold">
                            {children}
                          </th>
                        );
                      },
                      td({ children }) {
                        return (
                          <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs">
                            {children}
                          </td>
                        );
                      },
                      blockquote({ children }) {
                        return (
                          <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-3 italic my-2 text-gray-600 dark:text-gray-400">
                            {children}
                          </blockquote>
                        );
                      },
                    }}
                  >
                    {citation.content}
                  </MemoizedReactMarkdown>
                </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 flex-1 flex items-center justify-center rounded-md h-full">
              No content available for this citation.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
